import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import AISparkIcon from "./AISparkIcon";

export function dockAIOrb(x, y, width, height, notification) {
  const diameter = 48, margin = 16, gap = 12;
  const left = x + diameter / 2 < width / 2 ? margin : Math.max(margin, width - margin - diameter);
  const maximum = Math.max(margin, height - margin - diameter);
  let top = Math.max(margin, Math.min(y, maximum));
  if (notification && left < notification.right + gap && left + diameter > notification.left - gap && top < notification.bottom + gap && top + diameter > notification.top - gap) {
    const above = notification.top - gap - diameter, below = notification.bottom + gap;
    const choices = [above, below].filter((candidate) => candidate >= margin && candidate <= maximum);
    if (choices.length) top = choices.reduce((best, candidate) => Math.abs(candidate - top) < Math.abs(best - top) ? candidate : best, choices[0]);
  }
  return { x: left, y: top };
}

const viewportWidth = () => document.documentElement.clientWidth || window.innerWidth;

export default function AdminAIOrb({ initialPosition, onExpand }) {
  const [position, setPosition] = useState(() => {
    try { const saved = JSON.parse(sessionStorage.getItem("admin-ai-orb") || "null"); if (Number.isFinite(saved?.x) && Number.isFinite(saved?.y)) return saved; } catch {}
    return initialPosition;
  });
  const [dragging, setDragging] = useState(false);
  const current = useRef(position);
  const drag = useRef(null);
  const suppressClick = useRef(false);
  const settle = useCallback(() => {
    const next = dockAIOrb(current.current.x, current.current.y, viewportWidth(), window.innerHeight, document.querySelector(".announcement-fab")?.getBoundingClientRect());
    current.current = next; setPosition(next);
    try { sessionStorage.setItem("admin-ai-orb", JSON.stringify(next)); } catch {}
  }, []);
  useLayoutEffect(() => {
    settle();
    const update = () => {
      if (!drag.current && !document.querySelector(".announcement-fab")?.classList.contains("dragging")) settle();
    };
    // The page scrollbar can appear after the orb mounts without a window resize.
    // Re-dock when the usable page width changes, using the same drag/drop spacing.
    let lastWidth = viewportWidth();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => {
      const width = viewportWidth();
      if (width !== lastWidth) { lastWidth = width; update(); }
    });
    observer?.observe(document.documentElement);
    window.addEventListener("resize", update);
    return () => { observer?.disconnect(); window.removeEventListener("resize", update); };
  }, [settle]);
  const finish = () => {
    if (!drag.current) return;
    const moved = drag.current.moved;
    drag.current = null; setDragging(false);
    if (moved) settle();
  };
  return <button type="button" data-no-standard-button="true" className={`admin-ai-orb${dragging ? " is-dragging" : ""}`}
    style={{ left: position.x, top: position.y }} aria-label="Expand chat" title="Open HR Assistant — drag to either edge"
    onPointerDown={(event) => {
      if (event.button !== 0) return;
      suppressClick.current = false;
      drag.current = { x: event.clientX, y: event.clientY, left: current.current.x, top: current.current.y, moved: false };
      event.currentTarget.setPointerCapture(event.pointerId);
    }}
    onPointerMove={(event) => {
      const origin = drag.current;
      if (!origin || (!origin.moved && Math.hypot(event.clientX - origin.x, event.clientY - origin.y) < 5)) return;
      origin.moved = true; suppressClick.current = true; setDragging(true);
      const next = { x: Math.max(8, Math.min(origin.left + event.clientX - origin.x, viewportWidth() - 56)), y: Math.max(16, Math.min(origin.top + event.clientY - origin.y, window.innerHeight - 64)) };
      current.current = next; setPosition(next);
    }}
    onPointerUp={finish} onPointerCancel={finish} onLostPointerCapture={finish}
    onClick={() => { if (suppressClick.current) { suppressClick.current = false; return; } onExpand(); }}><AISparkIcon /></button>;
}
